import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getDocumentStatus, type DocumentJobStatusResponse } from '../api';
import { reviewerQueryKeys } from './useDocumentResult';

/** Statuses for which we still poll the backend. */
const IN_FLIGHT_STATUSES = new Set<DocumentJobStatusResponse['status']>(['queued', 'processing']);

const POLL_MS = 2_000;

/**
 * Polls `GET /documents/{id}/status` every 2s while the job is in flight.
 * Once the status becomes terminal (approved / review_required / rejected /
 * failed), the refetch interval is disabled so we don't spam the API.
 */
export function useDocumentStatus(jobId: string): UseQueryResult<DocumentJobStatusResponse> {
  return useQuery({
    queryKey: reviewerQueryKeys.status(jobId),
    queryFn: () => getDocumentStatus(jobId),
    enabled: jobId.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) {
        return POLL_MS;
      }
      return IN_FLIGHT_STATUSES.has(data.status) ? POLL_MS : false;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: (failureCount, error) => {
      // Bail out fast on 4xx; the apiFetch layer turns 401 into a logout.
      const status = (error as { status?: number } | undefined)?.status;
      if (status !== undefined && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function isTerminalStatus(status: DocumentJobStatusResponse['status'] | undefined): boolean {
  return status !== undefined && !IN_FLIGHT_STATUSES.has(status);
}
